import React, { useContext, useEffect, useState, useMemo } from "react";
import { base_url } from "../utils/axiosConfig";
import { CurrencyContext } from "../features/context/CurrencyContext";
import { useNavigate } from "react-router-dom";

function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { convertPrice, getCurrencySymbol } = useContext(CurrencyContext);
  const navigate = useNavigate();

  const [sortOption, setSortOption] = useState("default");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const hasRepeatedDigits = (price) => {
    if (!price) return true;
    return /(\d)\1{4,}/.test(String(price));
  };

  const isValidProduct = (product) => {
    const price =
      product?.price?.amount ?? product?.variations?.[0]?.price?.amount;
    return price && !hasRepeatedDigits(price);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${base_url}product/getCategory?page=1&limit=100&sortingColumn=createdAt&sortingOrder=asc`,
        );

        if (!res.ok) throw new Error("Failed to fetch products");

        const data = await res.json();

        const allProducts =
          data?.data?.flatMap((category) => {
            if (!category?.isActive) return [];

            return (
              category?.subCategories
                ?.filter((sub) => sub?.isActive)
                ?.flatMap((sub) => {
                  const catId = category._id;
                  const subId = sub._id;

                  const direct = (sub?.products || [])
                    .filter((p) => p?.isActive)
                    .map((p) => ({
                      ...p,
                      categoryId: catId,
                      subCategoryId: subId,
                    }));

                  const subSub = (sub?.subSubCategories || [])
                    .filter((ss) => ss?.isActive)
                    .flatMap((ss) => {
                      const ssId = ss._id;
                      return [
                        ...(ss?.products || [])
                          .filter((p) => p?.isActive)
                          .map((p) => ({
                            ...p,
                            categoryId: catId,
                            subCategoryId: subId,
                            subSubCategoryId: ssId,
                          })),
                        ...(ss?.subSubSubCategories || [])
                          .filter((sss) => sss?.isActive)
                          .flatMap((sss) =>
                            (sss?.products || [])
                              .filter((p) => p?.isActive)
                              .map((p) => ({
                                ...p,
                                categoryId: catId,
                                subCategoryId: subId,
                                subSubCategoryId: ssId,
                                subSubSubCategoryId: sss._id,
                              })),
                          ),
                      ];
                    });

                  return [...direct, ...subSub];
                }) || []
            );
          }) || [];

        setProducts(allProducts.filter(isValidProduct));
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((p) =>
        p.productName?.toLowerCase().includes(term),
      );
    }

    if (minPrice) {
      result = result.filter((p) => {
        const price =
          p?.price?.amount ?? p?.variations?.[0]?.price?.amount ?? 0;
        return price >= Number(minPrice);
      });
    }
    if (maxPrice) {
      result = result.filter((p) => {
        const price =
          p?.price?.amount ?? p?.variations?.[0]?.price?.amount ?? 0;
        return price <= Number(maxPrice);
      });
    }

    if (sortOption === "low-high") {
      result.sort((a, b) => {
        const pa = a?.price?.amount ?? a?.variations?.[0]?.price?.amount ?? 0;
        const pb = b?.price?.amount ?? b?.variations?.[0]?.price?.amount ?? 0;
        return pa - pb;
      });
    } else if (sortOption === "high-low") {
      result.sort((a, b) => {
        const pa = a?.price?.amount ?? a?.variations?.[0]?.price?.amount ?? 0;
        const pb = b?.price?.amount ?? b?.variations?.[0]?.price?.amount ?? 0;
        return pb - pa;
      });
    }

    return result;
  }, [products, searchTerm, sortOption, minPrice, maxPrice]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="h-12 w-12 border-4 border-[#35A93E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error)
    return <p className="text-center text-red-600 mt-20">Error: {error}</p>;

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Shop</h1>
          <p className="text-gray-600 text-sm mt-1">
            Discover our premium collection
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-2xl shadow-sm mb-8 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#35A93E]"
            />
            <span className="absolute left-3.5 top-3 text-gray-400">🔍</span>
          </div>

          <select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            className="border border-gray-200 rounded-xl px-2 py-2.5 text-sm focus:outline-none focus:border-[#35A93E]"
          >
            <option value="default">Relevance</option>
            <option value="low-high">Low to High</option>
            <option value="high-low">High to Low</option>
          </select>

          <div className="flex items-center gap-2">
            <input
              type="number"
              placeholder="Min"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              className="w-36 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#35A93E]"
            />
            <span className="text-gray-400">-</span>
            <input
              type="number"
              placeholder="Max"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              className="w-36 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#35A93E]"
            />
          </div>

          <button
            onClick={() => {
              setSearchTerm("");
              setMinPrice("");
              setMaxPrice("");
              setSortOption("default");
            }}
            className="text-red-600 hover:text-red-700 text-sm font-medium px-4 py-2.5"
          >
            Clear
          </button>
        </div>

        <p className="text-gray-600 text-sm mb-6">
          Showing{" "}
          <span className="font-semibold">{filteredProducts.length}</span>{" "}
          products
        </p>

        {/* Product Grid */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl">
            <p className="text-xl text-gray-400">No products found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5 gap-4">
            {filteredProducts.map((product) => {
              const price =
                product?.price?.amount ??
                product?.variations?.[0]?.price?.amount;

              return (
                <div
                  key={product._id}
                  onClick={() =>
                    navigate(`/product-details/${product._id}`, {
                      state: {
                        product,
                        category: product.categoryId,
                        subCategory: product.subCategoryId || null,
                        subSubCategory: product.subSubCategoryId || null,
                        subSubSubCategory: product.subSubSubCategoryId || null,
                      },
                    })
                  }
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-100 hover:border-gray-200 group"
                >
                  {/* Bigger Image */}
                  <div className="h-40 sm:h-44 md:h-48 lg:h-60 bg-gray-100 relative overflow-hidden">
                    <img
                      src={
                        product?.variations?.[0]?.images?.[0] ||
                        product?.images?.[0]
                      }
                      alt={product.productName}
                      className="absolute inset-0 w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>

                  {/* Name + Price in One Line */}
                  <div className="p-3 flex justify-between items-start gap-2">
                    <h3 className="text-xs font-medium line-clamp-2 flex-1 text-gray-900 leading-tight">
                      {product.productName}
                    </h3>
                    <p className="text-base font-semibold text-gray-900 whitespace-nowrap">
                      {getCurrencySymbol()}
                      {convertPrice(price)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Shop;
